import React, { useRef } from 'react';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import { DayProps, useDayRender, DateRange as DateRangePickerType } from 'react-day-picker';
import isBefore from 'date-fns/isBefore';
import isAfter from 'date-fns/isAfter';
import differenceInCalendarDays from 'date-fns/differenceInCalendarDays';
import addMonths from 'date-fns/addMonths';
import subMonths from 'date-fns/subMonths';
import isPast from 'date-fns/isPast';
import { grey } from '@mui/material/colors';
import { styled } from '@guest-widgets/core';

export const CalendarDayPicker = ({ date, displayMonth }: DayProps) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { buttonProps, isHidden, selectedDays } = useDayRender(date, displayMonth, buttonRef);

  const to = (selectedDays as DateRangePickerType)?.to;
  const from = (selectedDays as DateRangePickerType)?.from;
  const maxDate = maxAllowedDate(from);
  const minDate = minAllowedDate(from);

  const isDisabled =
    differenceInCalendarDays(minDate, date) > 0 ||
    (maxDate && differenceInCalendarDays(maxDate, date) < 0);

  const isSelected =
    (!!from && differenceInCalendarDays(date, from) === 0) ||
    (!!to && differenceInCalendarDays(date, to) === 0);

  const isBetween = !!from && isAfter(date, from) && (!to || isBefore(date, to));

  if (isHidden) return <></>;

  return (
    <Box sx={{ mx: -4, background: isBetween ? grey[200] : undefined }}>
      <StyledButton
        color="secondary"
        variant={isSelected ? 'contained' : 'text'}
        ref={buttonRef}
        {...buttonProps}
        disabled={isDisabled}
        disableElevation
      />
    </Box>
  );
};

const GUEST_WIDGET_MAX_DATE_RANGE_MONTH = 3;

/**It gets the maximum allowed date selection */
export const maxAllowedDate = (date?: Date) =>
  date ? addMonths(date, GUEST_WIDGET_MAX_DATE_RANGE_MONTH) : undefined;

/**It gets the minimum allowed date selection */
export const minAllowedDate = (date: Date = new Date()) => {
  const minDate = subMonths(date, GUEST_WIDGET_MAX_DATE_RANGE_MONTH);
  return isPast(minDate) ? new Date() : minDate;
};

const StyledButton = styled(Button)({
  minWidth: '3.5ch',
  width: '61%',
  margin: 0,
  '& > span': {
    opacity: 0.55,
  },
});
